import { format, parse } from 'date-fns'
import axios from 'axios'
import { Session } from 'next-auth'
import qs from 'qs'
import { SearchParams, SelfDispenseFilterValue } from '@/lib/types'
import {
  AuthenticationErrorType,
  AvailableScheduleResponse,
  ConsultationType,
  ConversionData,
  ElectronicsAddressSource,
  IHIdentifier,
  PbsRestrictionType,
  PbsType,
  StoryClass,
  StoryStage,
  TreatmentType,
} from '@/lib/apis/types'
import type { Metadata } from 'next'
import { cmsFetchPageMetadata } from '@/lib/cms'
import {
  postCountQuery,
  sanityFetch,
} from '@/lib/apis/sanity'
import { PortableTextTextBlock } from '@sanity/types'
import { AppointmentDate } from '@/features/common-requests/call-arrangement-context'

const DATE_FORMAT = 'dd/MM/yyyy'

export function formatDate(date: Date, df: string = DATE_FORMAT) {
  return format(date, df)
}

export function formatTime(date: Date): string {
  return format(date, 'MMM dd, yyyy, HH:mm:ss aaa')
}

export function parseDate(data: string, df: string = DATE_FORMAT): Date {
  return parse(data, df, new Date())
}

export function axiosErrorToString(error: any, message?: string) {
  if (axios.isAxiosError(error) && error.response?.data.message) {
    return error.response.data.message
  } else if (message) {
    return message
  } else {
    return 'Something went wrong'
  }
}

export function user(session: Session | null) {
  return {
    containsAny: (roles: string[]): boolean => {
      for (let role of session?.user?.roles ?? []) {
        if (roles.includes(role)) return true
      }

      return false
    },
    contains: (role: string): boolean => {
      return (session?.user?.roles ?? []).includes(role)
    },
  }
}

export function buildUrlWithParamsUpdated(
  url: string,
  kvPairs: [string, string | null][],
  params?: SearchParams,
) {
  params = { ...params }
  for (let [k, v] of kvPairs) {
    if (!v) delete params[k]
    else params[k] = v
  }

  const pstr = qs.stringify(params, { indices: false })
  const delim = pstr ? '?' : ''

  return `${url}${delim}${pstr}`
}

export function buildPaginationUrl(
  url: string,
  index: number,
  params?: SearchParams,
) {
  if (index === 0) {
    return buildUrlWithParamsUpdated(url, [['page', null]], params)
  } else {
    return buildUrlWithParamsUpdated(url, [['page', `${index}`]], params)
  }
}

export function parsePageNumberFromParams(params?: SearchParams): number {
  if (params && 'page' in params) {
    return parseInt(params['page'] as string)
  }

  return 0
}

const IHI_LOOKUP = [
  [0, 2, 4, 6, 8, 1, 3, 5, 7, 9],
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
]

// Validate the given IHI number and return an error message if not
export function checkLuhn(
  value: string,
  category: 'IHI' | 'HPI-I' | 'HPI-O',
): string | null {
  if (value.length !== 16 || !value.startsWith('80036'))
    return `${category} Number is 16-character long and always starts with 80036.`

  // Check if only numbers
  for (let i = 0; i < value.length; i++) {
    const char = value.charAt(i)
    if (char < '0' || char > '9')
      return `${category} Number consists of only numbers, without spaces.`
  }

  // The 6th digit is -
  // 0 for IHI
  // 1 for HPI-I
  // 2 for HPI-O
  const actualSixthChar = value.charAt(5)
  let expectedSixthChar
  switch (category) {
    case 'IHI':
      expectedSixthChar = '0'
      break
    case 'HPI-I':
      expectedSixthChar = '1'
      break
    case 'HPI-O':
      expectedSixthChar = '2'
      break
  }
  if (actualSixthChar !== expectedSixthChar) {
    return `Please enter correct ${category} number.`
  }

  const prefix = value.substring(0, 15)
  let total = 0
  for (let i = 0; i < prefix.length; i++) {
    const arrIdx = i % 2
    const chrIdx = Number(prefix.charAt(i))
    total += IHI_LOOKUP[arrIdx][chrIdx]
  }

  const mod = total % 10
  const check = mod === 0 ? 0 : 10 - mod
  if (check !== Number(value.charAt(value.length - 1)))
    return `Please enter correct ${category} number.`

  return null
}

export function checkMedicare(value: string): string | null {
  if (value.length !== 10)
    return 'Medicare number is 10-character long. Please avoid spaces.'

  for (let i = 0; i < value.length; i++) {
    const char = value.charAt(i)
    if (char < '0' || char > '9')
      return 'Please enter only numbers, without spaces.'
  }

  const total =
    Number(value.charAt(0)) +
    Number(value.charAt(1)) * 3 +
    Number(value.charAt(2)) * 7 +
    Number(value.charAt(3)) * 9 +
    Number(value.charAt(4)) +
    Number(value.charAt(5)) * 3 +
    Number(value.charAt(6)) * 7 +
    Number(value.charAt(7)) * 9
  const rem = total % 10
  if (rem !== Number(value.charAt(8)))
    return 'Provided Medicare Number is incorrect.'

  return null
}

export function checkMedicareExpiry(value: string): string | null {
  const err = 'Expiry must be in the format MM/YYYY.'
  if (value.length !== 7) return err

  const parts = value.split('/')
  if (parts.length !== 2 || parts[0].length !== 2 || parts[1].length !== 4)
    return err

  const month = Number(parts[0])
  const year = Number(parts[1])
  if (Number.isNaN(month) || Number.isNaN(year) || month < 1 || month > 12)
    return err
  const currentDate = new Date()
  const currentYear = currentDate.getFullYear()
  const currentMonth = currentDate.getMonth() + 1
  if (year < currentYear || (year === currentYear && month < currentMonth))
    return 'Your medicare card has expired.'

  return null
}

export function formatIHINumber(value: string): string {
  return `${value.substring(0, 4)} ${value.substring(4, 8)} ${value.substring(
    8,
    12,
  )} ${value.substring(12, 16)}`
}

export function formatMedicareNumber(value: string): string {
  return `${value.substring(0, 4)} ${value.substring(4, 8)} ${value.substring(
    8,
    9,
  )} ${value.substring(9, 10)}`
}

export function formatMobileNumber(mobile: string): string {
  return `${mobile.substring(0, 4)} ${mobile.substring(4, 7)} ${mobile.substring(7)}`
}

export function cleanIHI(identifier: IHIdentifier): IHIdentifier {
  switch (identifier.type) {
    case 'ihi':
      return { ...identifier, ihi: identifier.ihi.replace(/ /g, '') }
    case 'med':
      return { ...identifier, number: identifier.number.replace(/ /g, '') }
    case 'dva':
      return { ...identifier, number: identifier.number.replace(/ /g, '') }
  }
}

export async function fetchMetadata(url: string): Promise<Metadata> {
  const page = await cmsFetchPageMetadata(url)
  return {
    title: page.title,
    description: page.description,
  }
}

export function containsDigit(input: string): boolean {
  for (let i = 0; i < input.length; i++) {
    if (input.charAt(i).match(/[0-9]/)) return true
  }

  return false
}

export function containsLetter(input: string): boolean {
  for (let i = 0; i < input.length; i++) {
    if (input.charAt(i).match(/[a-z]/i)) return true
  }

  return false
}

export function passwordIsValid(password: string): boolean {
  return (
    password.length >= 8 && containsDigit(password) && containsLetter(password)
  )
}

export function downloadAsJson(json: any, filename: string) {
  const data = JSON.stringify(json)
  const blob = new Blob([data], { type: 'application/json' })
  const jsonObjectUrl = URL.createObjectURL(blob)

  const anchorEl = document.createElement('a')
  anchorEl.href = jsonObjectUrl
  anchorEl.download = filename
  anchorEl.click()
  URL.revokeObjectURL(jsonObjectUrl)
}

export function blocksToText(blocks: Array<PortableTextTextBlock>) {
  const options = { nonTextBehavior: 'remove' }
  return blocks
    .map((block) => {
      if (block._type !== 'block' || !block.children) {
        return options.nonTextBehavior === 'remove'
          ? ''
          : `[${block._type} block]`
      }

      return block.children.map((child) => child.text).join('')
    })
    .join('\n\n')
}

export function effectiveMedicineName(
  genericName: string,
  productName: string | null,
  brand: string | null,
  isLemi: boolean,
): string {
  if (isLemi) {
    if (!productName) throw new Error('Product name is missing for a LEMI item')
    else return productName
  } else if (!brand) return genericName
  else return `${genericName} (${brand})`
}

export function isAlphabet(input: string): boolean {
  for (let idx = 0; idx < input.length; idx++) {
    const code = input.charCodeAt(idx)
    if (!((code >= 97 && code <= 122) || (code >= 65 && code <= 90))) {
      return false
    }
  }

  return true
}

export function isAlphaNumeric(input: string): boolean {
  for (let idx = 0; idx < input.length; idx++) {
    const code = input.charCodeAt(idx)
    if (!charCodeIsAlphaNumeric(code)) {
      return false
    }
  }

  return true
}

export function charCodeIsAlphaNumeric(code: number): boolean {
  return (
    (code >= 97 && code <= 122) ||
    (code >= 65 && code <= 90) ||
    (code >= 48 && code <= 57)
  )
}

export function isNumber(input: string): boolean {
  if (input.length === 0) return false

  // Need to check each char code because `parseInt` only parse the prefix and ignore the rest if there are alphabets.
  // Which means 10ml will return as not a number even though it should be
  let start = 0
  if (input.startsWith('+') || input.startsWith('-')) start++

  let decimal = false

  for (let idx = start; idx < input.length; idx++) {
    const code = input.charCodeAt(idx)
    if (code < 48 || code > 57) {
      if (code === 46) {
        if (decimal) return false

        decimal = true
        continue
      }

      return false
    }
  }

  return true
}

export function pbsTypeToString(type: PbsType): string {
  switch (type) {
    case 'RPBS':
      return 'RPBS'
    case 'PBS_RPBS':
      return 'PBS/RPBS'
    case 'ExtemporaneousPreparations':
      return 'Extemporaneous Preparations'
    case 'PalliativeCare':
      return 'Palliative Care'
    case 'PrescriberBag':
      return 'Prescriber Bag (Dr Bag)'
    case 'Section_100':
      return 'Section 100'
  }
}

export function pbsRestrictionTypeToString(type: PbsRestrictionType): string {
  switch (type) {
    case 'StreamlinedAuthority':
      return 'Streamlined Authority'
    case 'OnlineAuthorityRequired':
      return 'Online Authority Required'
    case 'WrittenAuthorityRequired':
      return 'Written Authority Required'
    default:
      return type
  }
}

export function authorityNumberRequired(
  type: PbsRestrictionType | null,
): boolean {
  if (type === null) return false

  switch (type) {
    case 'WrittenAuthorityRequired':
    case 'OnlineAuthorityRequired':
    case 'StreamlinedAuthority':
      return true
    default:
      return false
  }
}

export type AddressType =
  | 'PATIENT_EMAIL'
  | 'PATIENT_MOBILE'
  | 'EMAIL'
  | 'MOBILE'
  | 'NO_TOKEN'

export function createEScriptAddressSource(
  addressType: AddressType,
  address: string,
): ElectronicsAddressSource {
  switch (addressType) {
    case 'PATIENT_EMAIL':
      return { type: 'patient_email' }

    case 'PATIENT_MOBILE':
      return { type: 'patient_mobile' }

    case 'EMAIL':
      return { type: 'email', email: address }

    case 'MOBILE':
      return { type: 'mobile', sms: address }

    case 'NO_TOKEN':
      return { type: 'no_token' }

    default:
      throw new Error('This should not happen')
  }
}

export async function generateBlogPostSitemapIds(): Promise<Array<number>> {
  const totalPosts = await sanityFetch<number>({ query: postCountQuery })
  const totalPages = Math.ceil(totalPosts / 50000)
  return Array.from({ length: totalPages }, (_, i) => i)
}

export function consultationTypes(): Array<ConsultationType> {
  return ['MC', 'PS', 'GC', 'SR', 'TR']
}

export function computeAppointmentDates(
  response: AvailableScheduleResponse,
): Array<AppointmentDate> {
  return response.days.map((day) => ({
    day: day.day[0] + day.day.slice(1, 3).toLowerCase(),
    date: day.date,
    availableTimes: day.slots.map((slot) => {
      const date = new Date(slot)
      const hours = `${date.getHours()}`.padStart(2, '0')
      const minutes = `${date.getMinutes()}`.padEnd(2, '0')
      return {
        time: `${hours}:${minutes}`,
        millis: slot,
      }
    }),
  }))
}

export function translateAuthenticationError(
  error: AuthenticationErrorType,
): string {
  switch (error) {
    case 'INVALID_CREDENTIALS':
      return 'Wrong Email or Password'
    case 'VULNERABLE_CREDENTIALS':
      return 'Your password has been breached. Please contact support to reset your password immediately.'
    case 'THIRD_PARTY_UNAVAILABLE':
      return 'Authentication service not available now. Please try again later or contact support@nextclinic.com.au.'
    case 'NETWORK_ERROR':
      return 'Network error, are you connected to the internet? Please try again.'
  }
}

export function translateTreatmentType(type?: TreatmentType | null): string {
  switch (type) {
    case 'ErectileDysfunction':
      return 'Erectile dysfunction'
    case 'PrematureEjaculation':
      return 'Premature ejaculation'
    case 'MaleHairLoss':
      return 'Hair loss'
    case 'WeightLoss':
      return 'Weight loss'
    default:
      return 'Unknown'
  }
}

export function translateStoryClass(clazz?: StoryClass | null) {
  switch (clazz) {
    case 'InitialRequest':
      return 'Initial request'
    case 'PlanReview':
      return 'Yearly review'
    default:
      return 'Unknown'
  }
}

export function translateStoryStage(stage?: StoryStage | null) {
  switch (stage) {
    case 'PendingReview':
      return 'Pending practitioner review'
    case 'Rejected':
      return 'Rejected'
    case 'PatientAccepted':
      return 'Patient accepted'
    case 'IncompleteQuiz':
      return 'Incomplete quiz'
    case 'PaymentExpired':
      return 'Payment expired'
    case 'PatientRejected':
      return 'Patient rejected'
    case 'Cancelled':
      return 'Cancelled'
    case 'PendingPatientAcceptance':
      return 'Pending patient review'
    case 'PaymentProcessing':
      return 'Processing payment'
    default:
      return 'Unknown'
  }
}

export function mapSelfDispenseFilter(
  value: SelfDispenseFilterValue,
): boolean | null {
  if (value === 'self') return true
  if (value === 'pharmacy') return false
  return null
}

export function createConversionEvent(
  transactionId: string,
  productType: string,
  value: number,
  data: ConversionData,
  productClass?: string,
): Record<string, any> {
  return {
    product_type: productType,
    product_class: productClass,
    value,
    currency: 'AUD',
    transaction_id: transactionId,
    user_email: data.email,
    user_phone: data.phoneNumber,
    user_first_name: data.firstName,
    user_last_name: data.lastName,
    user_street: data.street,
    user_city: data.city,
    user_region: data.region,
    user_country: data.country,
    user_postal_code: data.postalCode,

    // This is for automated Google enhanced-conversion, but not sure if it's
    // actually used.
    user_data: {
      email: data.email,
      phone_number: data.phoneNumber,
      first_name: data.firstName,
      last_name: data.lastName,
      street: data.street,
      city: data.city,
      region: data.region,
      country: data.country,
      postal_code: data.postalCode,
    },

    // This is for FB conversion events with hashed data
    fbud: {
      em: data.hem,
      ph: data.hph,
      fn: data.hfn,
      ln: data.hln,
      db: data.hdb,
      ge: data.hgn,
      ct: data.hct,
      st: data.hre,
      zp: data.hpt,
      country: data.hco,
    },

    // This is for the item field
    items: [
      {
        item_id: productType,
        item_name: productType,
        quantity: 1,
        price: value,
      },
    ],
  }
}
